import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useApp } from "@/contexts/AppContext";
import CampaignsPage from "./CampaignsPage";
import SegmentsPage from "./SegmentsPage";
import StatisticsPage from "./StatisticsPage";
import AnalyticsPage from "./AnalyticsPage";
import UsersPage from "./UsersPage";
import ProvidersPage from "./ProvidersPage";
import CreativesPage from "./CreativesPage";
import CampaignFieldDistribution from "@/components/campaigns/CampaignFieldDistribution";
import AppShell, { usePageTitle } from "@/components/common/AppShell";
import StatCard from "@/components/common/StatCard";
import CampaignsTable from "@/components/campaigns/CampaignsTable";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import DateRangePicker from "@/components/common/DateRangePicker";
import DevicesPage from "@/features/devices/pages/DevicesPage";
import DeviceDetailPage from "@/features/devices/pages/DeviceDetailPage";
import SegmentBuilderPage from "./SegmentBuilderPage";
import CampaignBuilderPage from "./CampaignBuilderPage";
import {
  LayoutDashboard,
  Megaphone,
  Users,
  Smartphone,
  BarChart3,
  LineChart,
  ShieldCheck,
  Eye,
  LogOut,
  Building2,
  Film,
  DollarSign,
  TrendingUp,
  UserCheck,
} from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  ChartTooltip,
  ChartLegend,
);

const CampaignAnalytics = ({ analytics, segments, days }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getSegmentName = (segmentId) => {
    const segment = segments.find((s) => s.uuid === segmentId);
    return segment?.name || segmentId?.slice(0, 8) || "Unknown";
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [analytics?.dailyBreakdown?.length]);

  const totalDays = analytics.dailyBreakdown?.length || 0;
  const totalPages = Math.ceil(totalDays / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDays =
    analytics.dailyBreakdown?.slice(startIndex, endIndex) || [];

  return (
    <div style={{ marginTop: "2rem" }}>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Ads Served</div>
          <div className="stat-value">
            {analytics.totalServed.toLocaleString("en-US")}
          </div>
          <div className="stat-change neutral">
            {days > 0 ? `Last ${days} days` : "All time"}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Unique Devices</div>
          <div className="stat-value">
            {analytics.uniqueDevices.toLocaleString("en-US")}
          </div>
          <div className="stat-change positive">
            {analytics.totalServed > 0
              ? `${(analytics.totalServed / analytics.uniqueDevices).toFixed(1)} avg per device`
              : "No data"}
          </div>
        </div>
      </div>

      {analytics.dailyBreakdown?.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h3 style={{ margin: 0, color: "var(--text-primary)" }}>
              Daily Breakdown
            </h3>
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "0.875rem",
                  color: "var(--text-secondary)",
                }}
              >
                <span>
                  Page {currentPage} of {totalPages}
                </span>
              </div>
            )}
          </div>
          <div className="campaigns-grid">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Served</th>
                  <th>Unique Devices</th>
                  <th>Avg per Device</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDays.map((day) => (
                  <tr key={day.date}>
                    <td>
                      {new Date(day.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                    <td>{day.served.toLocaleString("en-US")}</td>
                    <td>{day.uniqueDevices.toLocaleString("en-US")}</td>
                    <td>
                      {day.uniqueDevices > 0
                        ? (day.served / day.uniqueDevices).toFixed(2)
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.5rem",
                marginTop: "1rem",
              }}
            >
              <button
                className="btn"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{ minWidth: "80px" }}
              >
                Previous
              </button>
              <div style={{ display: "flex", gap: "0.25rem" }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          className={`btn ${currentPage === page ? "btn-primary" : ""}`}
                          onClick={() => setCurrentPage(page)}
                          style={{
                            minWidth: "36px",
                            padding: "0.5rem",
                            fontSize: "0.875rem",
                            backgroundColor:
                              currentPage === page
                                ? "var(--primary)"
                                : "var(--bg-secondary)",
                            color:
                              currentPage === page
                                ? "white"
                                : "var(--text-primary)",
                            border: "1px solid var(--border)",
                          }}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return (
                        <span key={page} style={{ padding: "0.5rem" }}>
                          ...
                        </span>
                      );
                    }
                    return null;
                  },
                )}
              </div>
              <button
                className="btn"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                style={{ minWidth: "80px" }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {analytics.segmentBreakdown?.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ marginBottom: "1rem", color: "var(--text-primary)" }}>
            By Segment
          </h3>
          <div className="campaigns-grid">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th>Served</th>
                  <th>Unique Devices</th>
                </tr>
              </thead>
              <tbody>
                {analytics.segmentBreakdown.map((seg) => (
                  <tr key={seg.segmentId}>
                    <td>{getSegmentName(seg.segmentId)}</td>
                    <td>{seg.served.toLocaleString("en-US")}</td>
                    <td>{seg.uniqueDevices.toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {analytics.totalServed === 0 && (
        <div className="empty-state" style={{ marginTop: "2rem" }}>
          <h3>No data yet</h3>
          <p>This campaign has not served any ads in the selected period.</p>
        </div>
      )}
    </div>
  );
};

const DEFAULT_RANGE_DAYS = 30;

const defaultDateRange = () => {
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  const from = new Date();
  from.setDate(from.getDate() - (DEFAULT_RANGE_DAYS - 1));
  from.setHours(0, 0, 0, 0);
  return { from, to };
};

const useStatsRange = (dateRange) =>
  useMemo(() => {
    const from =
      dateRange?.from instanceof Date ? new Date(dateRange.from) : new Date();
    const to =
      dateRange?.to instanceof Date ? new Date(dateRange.to) : new Date();
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [dateRange?.from, dateRange?.to]);

const rangeLabel = (dateRange) => {
  if (!dateRange?.from || !dateRange?.to) return "Selected range";
  const fmt = (d) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const sameYear = dateRange.from.getFullYear() === dateRange.to.getFullYear();
  return sameYear
    ? `${fmt(dateRange.from)} – ${fmt(dateRange.to)}, ${dateRange.to.getFullYear()}`
    : `${fmt(dateRange.from)}, ${dateRange.from.getFullYear()} – ${fmt(dateRange.to)}, ${dateRange.to.getFullYear()}`;
};

const useSpentRevenue = (performRequest, range) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    performRequest(
      `/statistics/spent-revenue?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
    )
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load spent-revenue stats", err))
      .finally(() => setLoading(false));
  }, [performRequest, range.from, range.to]);

  return { rows, loading };
};

const useCampaignTotals = (performRequest, range) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    performRequest(
      `/statistics/campaign-totals?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
    )
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load campaign-totals", err))
      .finally(() => setLoading(false));
  }, [performRequest, range.from, range.to]);

  return { rows, loading };
};

const useOverviewSummary = (performRequest, range) => {
  const [summary, setSummary] = useState({ impressions: 0, households: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    performRequest(
      `/statistics/overview-summary?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
    )
      .then((data) =>
        setSummary({
          impressions: Number(data?.impressions ?? 0),
          households: Number(data?.households ?? 0),
        }),
      )
      .catch((err) => console.error("Failed to load overview-summary", err))
      .finally(() => setLoading(false));
  }, [performRequest, range.from, range.to]);

  return { summary, loading };
};

const SpendOverTimeChart = ({ rows, loading }) => {
  const chartData = useMemo(() => {
    const labels = rows.map((r) =>
      new Date(r.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    );
    const filled = (color, bg) => ({
      borderColor: color,
      backgroundColor: bg,
      fill: true,
      tension: 0.35,
      borderWidth: 2,
      pointRadius: 3,
      pointBackgroundColor: color,
      pointBorderColor: "#fff",
      pointBorderWidth: 1.5,
    });
    return {
      labels,
      datasets: [
        {
          label: "Revenue",
          data: rows.map((r) => Number(r.revenue ?? 0)),
          order: 3,
          ...filled("#15a86b", "rgba(21, 168, 107, 0.10)"),
        },
        {
          label: "Margin",
          data: rows.map((r) => Number(r.revenue ?? 0) - Number(r.spent ?? 0)),
          order: 2,
          fill: false,
          borderColor: "#f59e0b",
          backgroundColor: "#f59e0b",
          borderWidth: 2.5,
          tension: 0.35,
          pointRadius: 3.5,
          pointBackgroundColor: "#f59e0b",
          pointBorderColor: "#fff",
          pointBorderWidth: 1.5,
        },
        {
          label: "Spent",
          data: rows.map((r) => Number(r.spent ?? 0)),
          order: 1,
          ...filled("#6d28f0", "rgba(109, 40, 240, 0.10)"),
        },
      ],
    };
  }, [rows]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#1e2233",
          padding: 12,
          titleFont: { size: 12, weight: 600 },
          bodyFont: { size: 12 },
          cornerRadius: 8,
          callbacks: {
            label: (ctx) => {
              const v = Number(ctx.parsed.y) || 0;
              return `${ctx.dataset.label}: $${v.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#8b91a3", font: { size: 11 } },
          border: { display: false },
        },
        y: {
          grid: { color: "#eceef2", drawBorder: false },
          ticks: {
            color: "#8b91a3",
            font: { size: 11 },
            callback: (v) =>
              v >= 1000 || v <= -1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`,
          },
          border: { display: false },
        },
      },
    }),
    [],
  );

  if (loading) {
    return (
      <div className="flex h-[260px] items-center justify-center text-(--text-muted)">
        Loading spend data…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex h-[260px] items-center justify-center text-(--text-muted)">
        No spend data for the last 30 days.
      </div>
    );
  }

  return (
    <div className="h-[260px]">
      <Line data={chartData} options={chartOptions} />
    </div>
  );
};

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const formatCurrency = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const TopClientsTable = ({ users, totals, loading }) => {
  const rows = useMemo(() => {
    return users
      .map((u) => {
        const userTotals = totals.filter((t) => t.userId === u.uuid);
        const totalCampaigns = userTotals.length;
        const activeCampaigns = userTotals.filter(
          (t) => t.status === "active",
        ).length;
        const impressions = userTotals.reduce(
          (sum, t) => sum + (t.impressions ?? 0),
          0,
        );
        const revenue = userTotals.reduce(
          (sum, t) => sum + (t.revenue ?? 0),
          0,
        );
        const spent = userTotals.reduce((sum, t) => sum + (t.spent ?? 0), 0);
        return {
          uuid: u.uuid,
          username: u.username,
          totalCampaigns,
          activeCampaigns,
          impressions,
          revenue,
          margin: revenue - spent,
        };
      })
      .filter((r) => r.totalCampaigns > 0)
      .sort((a, b) => {
        if (b.activeCampaigns !== a.activeCampaigns) {
          return b.activeCampaigns - a.activeCampaigns;
        }
        return b.margin - a.margin;
      })
      .slice(0, 5);
  }, [users, totals]);

  if (loading) {
    return (
      <div className="px-4 py-8 text-center text-(--text-muted)">
        Loading clients…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-(--text-muted)">
        No active clients yet.
      </div>
    );
  }

  return (
    <DataTable>
      <thead>
        <tr className={DATA_TABLE_CLASSES.headRow}>
          <th className={DATA_TABLE_CLASSES.th}>Client</th>
          <th className={DATA_TABLE_CLASSES.th}>Active</th>
          <th className={DATA_TABLE_CLASSES.th}>Campaigns</th>
          <th className={DATA_TABLE_CLASSES.th}>Impressions</th>
          <th className={DATA_TABLE_CLASSES.th}>Revenue</th>
          <th className={DATA_TABLE_CLASSES.th}>Margin</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.uuid} className={DATA_TABLE_CLASSES.bodyRow}>
            <td className={DATA_TABLE_CLASSES.td}>
              <div className="flex items-center gap-[13px]">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-(--pastel-violet-soft) text-[12px] font-bold text-(--brand-primary)">
                  {initialsOf(r.username)}
                </div>
                <div className="text-[13.5px] font-bold text-(--text-primary)">
                  {r.username}
                </div>
              </div>
            </td>
            <td
              className={`${DATA_TABLE_CLASSES.td} text-[13.5px] font-bold text-(--text-primary)`}
            >
              {r.activeCampaigns}
            </td>
            <td
              className={`${DATA_TABLE_CLASSES.td} text-[13.5px] font-bold text-(--text-primary)`}
            >
              {r.totalCampaigns}
            </td>
            <td
              className={`${DATA_TABLE_CLASSES.td} text-[13.5px] font-bold text-(--text-primary)`}
            >
              {r.impressions.toLocaleString("en-US")}
            </td>
            <td
              className={`${DATA_TABLE_CLASSES.td} text-[13.5px] font-bold text-(--text-primary)`}
            >
              {formatCurrency(r.revenue)}
            </td>
            <td className={DATA_TABLE_CLASSES.td}>
              <span
                className={`text-[13.5px] font-bold ${
                  r.margin >= 0
                    ? "text-(--status-success)"
                    : "text-(--status-danger)"
                }`}
              >
                {formatCurrency(r.margin)}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
};

const OverviewPage = () => {
  usePageTitle("Overview");
  const {
    campaigns,
    segments,
    users,
    usersLoading,
    performRequest,
    currentUserType,
    getCampaignStatistics,
  } = useApp();
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [days, setDays] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  const activeCampaigns = campaigns.filter((c) => c.status === "active").length;

  const statsRange = useStatsRange(dateRange);
  const rangeDisplay = rangeLabel(dateRange);
  const { rows: spendRows, loading: spendLoading } = useSpentRevenue(
    performRequest,
    statsRange,
  );
  const { rows: campaignTotals, loading: campaignTotalsLoading } =
    useCampaignTotals(performRequest, statsRange);
  const { summary: overviewSummary, loading: overviewSummaryLoading } =
    useOverviewSummary(performRequest, statsRange);

  const totalSpent = useMemo(
    () => campaignTotals.reduce((s, r) => s + (Number(r.spent) || 0), 0),
    [campaignTotals],
  );
  const totalRevenue = useMemo(
    () => campaignTotals.reduce((s, r) => s + (Number(r.revenue) || 0), 0),
    [campaignTotals],
  );
  const totalMargin = totalRevenue - totalSpent;
  const totalSpendImpressions = useMemo(
    () => campaignTotals.reduce((s, r) => s + (Number(r.impressions) || 0), 0),
    [campaignTotals],
  );
  // Transit money: `received`/`income` are null on rows predating price
  // capture (and 0 across a bucket with no transit serves). `hasTransit`
  // gates the tiles so purely-standard dashboards stay unchanged.
  const hasTransit = useMemo(
    () =>
      campaignTotals.some(
        (r) => r.received != null || r.income != null,
      ),
    [campaignTotals],
  );
  const totalReceived = useMemo(
    () => campaignTotals.reduce((s, r) => s + (Number(r.received) || 0), 0),
    [campaignTotals],
  );
  const totalIncome = useMemo(
    () => campaignTotals.reduce((s, r) => s + (Number(r.income) || 0), 0),
    [campaignTotals],
  );

  const compactFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      }),
    [],
  );
  const moneyCompactFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 2,
      }),
    [],
  );
  const formatCompact = (n) => compactFormatter.format(Number(n) || 0);
  const formatMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const formatMoneyCompact = (n) =>
    `$${moneyCompactFormatter.format(Number(n) || 0)}`;

  const fetchAnalytics = useCallback(async () => {
    if (!selectedCampaign) {
      setAnalytics(null);
      return;
    }

    setLoading(true);
    try {
      const daysParam = days > 0 ? `days=${days}` : "";
      const data = await performRequest(
        `/campaigns/${selectedCampaign}/analytics${daysParam ? `?${daysParam}` : ""}`,
      );
      setAnalytics(data);
    } catch (error) {
      console.error("Failed to fetch campaign analytics", error);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCampaign, days, performRequest]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const isAdmin = currentUserType === "admin";
  const activeClientsCount = useMemo(() => {
    if (!isAdmin) return 0;
    const activeUserIds = new Set(
      campaigns
        .filter((c) => c.status === "active")
        .map((c) => c.userId)
        .filter(Boolean),
    );
    return activeUserIds.size;
  }, [campaigns, isAdmin]);

  // Campaigns to surface in the "Active campaigns" section: active OR
  // completed, with [startDate, endDate] overlapping the selected stats
  // window. Stats themselves are fetched all-time per campaign (not bound
  // to statsRange) so the bar reflects the campaign's full lifetime.
  const eligibleCampaigns = useMemo(() => {
    const fromMs = new Date(statsRange.from).getTime();
    const toMs = new Date(statsRange.to).getTime();
    return campaigns.filter((c) => {
      if (c.status !== "active" && c.status !== "completed") return false;
      const start = c.startDate ? new Date(c.startDate).getTime() : null;
      const end = c.endDate ? new Date(c.endDate).getTime() : null;
      const startOK = start == null || start <= toMs;
      const endOK = end == null || end >= fromMs;
      return startOK && endOK;
    });
  }, [campaigns, statsRange.from, statsRange.to]);

  const [campaignAllTimeStats, setCampaignAllTimeStats] = useState({});
  const requestedStatsRef = useRef(new Set());
  useEffect(() => {
    eligibleCampaigns.forEach((c) => {
      if (!c.uuid || requestedStatsRef.current.has(c.uuid)) return;
      requestedStatsRef.current.add(c.uuid);
      getCampaignStatistics(c.uuid)
        .then((stats) =>
          setCampaignAllTimeStats((prev) => ({ ...prev, [c.uuid]: stats })),
        )
        .catch(() =>
          setCampaignAllTimeStats((prev) => ({
            ...prev,
            [c.uuid]: { totalReceivers: 0, uniqueReceivers: 0 },
          })),
        );
    });
  }, [eligibleCampaigns, getCampaignStatistics]);

  return (
    <section>
      <div className="mb-[26px] flex flex-wrap items-start justify-between gap-4">
        <p className="text-[13.5px] text-(--text-muted)">
          Platform summary and real-time performance ({rangeDisplay})
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <select
            className="rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) outline-none focus:border-(--brand-primary)"
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
          >
            <option value="">All campaigns overview</option>
            {campaigns.map((c) => (
              <option key={c.uuid} value={c.uuid}>
                {c.name}
              </option>
            ))}
          </select>
          {selectedCampaign && (
            <select
              className="rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) outline-none focus:border-(--brand-primary)"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
            >
              <option value={0}>All time</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          )}
        </div>
      </div>

      {!selectedCampaign ? (
        <>
          <div className="mb-[26px] grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <StatCard
              label="Total Spent"
              value={
                campaignTotalsLoading ? "…" : formatMoneyCompact(totalSpent)
              }
              trend={rangeDisplay}
              icon={<DollarSign size={22} strokeWidth={1.8} />}
              accent="violet"
            />
            <StatCard
              label="Revenue"
              value={
                campaignTotalsLoading ? "…" : formatMoneyCompact(totalRevenue)
              }
              trend={rangeDisplay}
              icon={<TrendingUp size={22} strokeWidth={1.8} />}
              accent="green"
            />
            <StatCard
              label="Margin"
              value={
                campaignTotalsLoading ? "…" : formatMoneyCompact(totalMargin)
              }
              trend={
                totalRevenue > 0
                  ? `${((totalMargin / totalRevenue) * 100).toFixed(1)}% margin`
                  : "—"
              }
              icon={<DollarSign size={22} strokeWidth={1.8} />}
              accent={totalMargin >= 0 ? "green" : "rose"}
            />
            <StatCard
              label="Active Campaigns"
              value={formatCompact(activeCampaigns)}
              trend={`${campaigns.length} total`}
              icon={<Megaphone size={22} strokeWidth={1.8} />}
              accent="violet"
            />
            <StatCard
              label="Impressions Served"
              value={
                overviewSummaryLoading
                  ? "…"
                  : formatCompact(overviewSummary.impressions)
              }
              trend={
                overviewSummaryLoading
                  ? "…"
                  : `${formatCompact(overviewSummary.households)} households`
              }
              icon={<Eye size={22} strokeWidth={1.8} />}
              accent="violet"
            />
            {isAdmin ? (
              <StatCard
                label="Active Clients"
                value={usersLoading ? "…" : formatCompact(activeClientsCount)}
                trend={`${users.length} total`}
                icon={<UserCheck size={22} strokeWidth={1.8} />}
                accent="green"
              />
            ) : (
              <StatCard
                label="Audiences"
                value={formatCompact(segments.length)}
                trend="Ready to activate"
                icon={<Users size={22} strokeWidth={1.8} />}
                accent="violet"
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-(--radius-lg) border border-(--border-strong) bg-(--surface-1) p-[22px]">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-[15px] font-bold text-(--text-primary)">
                    Spend over time
                  </h3>
                  <p className="mt-1 text-[12.5px] text-(--text-muted)">
                    Provider spend and impressions delivered, last 30 days
                  </p>
                </div>
                <div className="flex items-center gap-4 text-[12px] font-semibold text-(--text-secondary)">
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-2.5 w-2.5 rounded-full bg-(--brand-primary)"
                    />
                    Spent
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-2.5 w-2.5 rounded-full bg-(--status-success)"
                    />
                    Revenue
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-2.5 w-2.5 rounded-full bg-[#f59e0b]"
                    />
                    Margin
                  </span>
                </div>
              </div>
              <SpendOverTimeChart rows={spendRows} loading={spendLoading} />
              {!spendLoading &&
                !campaignTotalsLoading &&
                spendRows.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-4 border-t border-(--border-subtle) pt-4 text-[13px] sm:grid-cols-4">
                    <div>
                      <div className="text-[11.5px] font-semibold text-(--text-muted)">
                        Total spent
                      </div>
                      <div className="mt-0.5 text-[17px] font-extrabold text-(--text-primary)">
                        {formatMoney(totalSpent)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11.5px] font-semibold text-(--text-muted)">
                        Total revenue
                      </div>
                      <div className="mt-0.5 text-[17px] font-extrabold text-(--text-primary)">
                        {formatMoney(totalRevenue)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11.5px] font-semibold text-(--text-muted)">
                        Margin
                      </div>
                      <div
                        className={`mt-0.5 text-[17px] font-extrabold ${
                          totalMargin >= 0
                            ? "text-(--status-success)"
                            : "text-(--status-danger)"
                        }`}
                      >
                        {formatMoney(totalMargin)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11.5px] font-semibold text-(--text-muted)">
                        Impressions
                      </div>
                      <div className="mt-0.5 text-[17px] font-extrabold text-(--text-primary)">
                        {totalSpendImpressions.toLocaleString("en-US")}
                      </div>
                    </div>
                    {hasTransit && (
                      <>
                        <div>
                          <div className="text-[11.5px] font-semibold text-(--text-muted)">
                            Received
                          </div>
                          <div className="mt-0.5 text-[17px] font-extrabold text-(--text-primary)">
                            {formatMoney(totalReceived)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11.5px] font-semibold text-(--text-muted)">
                            Income
                          </div>
                          <div
                            className={`mt-0.5 text-[17px] font-extrabold ${
                              totalIncome >= 0
                                ? "text-(--status-success)"
                                : "text-(--status-danger)"
                            }`}
                          >
                            {formatMoney(totalIncome)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
            </div>

            {isAdmin && (
              <div className="rounded-(--radius-lg) border border-(--border-strong) bg-(--surface-1) p-[22px]">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-[15px] font-bold text-(--text-primary)">
                      Top Clients Overview
                    </h3>
                    <p className="mt-1 text-[12.5px] text-(--text-muted)">
                      Most active accounts by campaign volume
                    </p>
                  </div>
                </div>
                <TopClientsTable
                  users={users}
                  totals={campaignTotals}
                  loading={usersLoading || campaignTotalsLoading}
                />
              </div>
            )}
          </div>

          <div className="mt-[26px] rounded-(--radius-lg) border border-(--border-strong) bg-(--surface-1) p-[22px]">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-[15px] font-bold text-(--text-primary)">
                  Active campaigns
                </h3>
                <p className="mt-1 text-[12.5px] text-(--text-muted)">
                  Active or completed campaigns running within {rangeDisplay}.
                  Impressions and devices shown are for the full campaign
                  lifetime.
                </p>
              </div>
            </div>
            <CampaignsTable
              campaigns={eligibleCampaigns}
              users={users}
              segments={segments}
              stats={campaignAllTimeStats}
              showAuthor={isAdmin}
              emptyMessage="No active or completed campaigns overlap with this date range."
            />
          </div>
        </>
      ) : loading ? (
        <div className="rounded-(--radius-lg) border border-(--border-strong) bg-(--surface-1) p-10 text-center text-(--text-muted)">
          Loading campaign analytics…
        </div>
      ) : analytics ? (
        <div className="flex flex-col gap-6">
          <CampaignAnalytics
            analytics={analytics}
            segments={segments}
            days={days}
          />
          <CampaignFieldDistribution
            campaignId={selectedCampaign}
            campaignName={
              campaigns.find((c) => c.uuid === selectedCampaign)?.name ||
              "Campaign"
            }
            days={days}
          />
        </div>
      ) : (
        <div className="rounded-(--radius-lg) border border-(--border-strong) bg-(--surface-1) p-10 text-center">
          <h3 className="text-base font-semibold text-(--text-primary)">
            Unable to load analytics
          </h3>
          <p className="mt-1 text-sm text-(--text-muted)">
            Please try again later.
          </p>
        </div>
      )}
    </section>
  );
};

export default function Dashboard() {
  const { currentUser, currentUserType, logout } = useApp();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const sections = [
    {
      items: [
        {
          to: "/dashboard",
          label: "Overview",
          icon: LayoutDashboard,
          end: true,
        },
        { to: "/dashboard/campaigns", label: "Campaigns", icon: Megaphone },
        { to: "/dashboard/segments", label: "Audiences", icon: Users },
      ],
    },
    {
      heading: "Traffic",
      items: [{ to: "/dashboard/devices", label: "Devices", icon: Smartphone }],
    },
    {
      heading: "Insights",
      items: [
        { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
        { to: "/dashboard/statistics", label: "Statistics", icon: LineChart },
      ],
    },
    {
      heading: "Assets",
      items: [{ to: "/dashboard/creatives", label: "Creatives", icon: Film }],
    },
    ...(currentUserType === "admin"
      ? [
          {
            heading: "Administration",
            items: [
              {
                to: "/dashboard/providers",
                label: "Publishers",
                icon: Building2,
              },
              { to: "/dashboard/users", label: "Users", icon: ShieldCheck },
            ],
          },
        ]
      : []),
  ];

  const routeTitles = {
    "/dashboard": "Overview",
    "/dashboard/campaigns": "Campaigns",
    "/dashboard/segments": "Audiences",
    "/dashboard/devices": "Devices",
    "/dashboard/analytics": "Analytics",
    "/dashboard/statistics": "Statistics",
    "/dashboard/users": "Users",
  };

  const sidebarFooter = (
    <div className="flex items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-2) px-3 py-2">
      <span
        aria-hidden="true"
        className="inline-flex h-2 w-2 rounded-(--radius-pill) bg-(--status-success)"
      />
      <div className="min-w-0 text-xs">
        <div className="font-semibold text-(--text-primary)">System status</div>
        <div className="text-(--text-muted)">All systems operational</div>
      </div>
    </div>
  );

  return (
    <AppShell
      brand="Adlywise DSP"
      brandSubtitle="CTV/OTT Network"
      roleLabel={currentUserType === "admin" ? "Admin" : undefined}
      sections={sections}
      sidebarFooter={sidebarFooter}
      routeTitles={routeTitles}
      user={{ name: currentUser, role: currentUserType }}
      userMenuItems={[{ label: "Logout", icon: LogOut, onClick: handleLogout }]}
    >
      <Routes>
        <Route index element={<OverviewPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        <Route path="campaigns/new" element={<CampaignBuilderPage />} />
        <Route path="campaigns/:uuid/edit" element={<CampaignBuilderPage />} />
        <Route path="segments" element={<SegmentsPage />} />
        <Route path="segments/new" element={<SegmentBuilderPage />} />
        <Route path="segments/:uuid/edit" element={<SegmentBuilderPage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route path="device/:deviceId" element={<DeviceDetailPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="statistics" element={<StatisticsPage />} />
        <Route path="creatives" element={<CreativesPage />} />
        {currentUserType === "admin" && (
          <Route path="users" element={<UsersPage />} />
        )}
        {currentUserType === "admin" && (
          <Route path="providers" element={<ProvidersPage />} />
        )}
      </Routes>
    </AppShell>
  );
}
